import { Component, OnDestroy, OnInit } from '@angular/core';
import { SortDirection } from '@angular/material/sort';
import { ActivatedRoute, Data, Router } from '@angular/router';
import { forkJoin, Observable, Subject } from 'rxjs';
import { filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { DataRequestState } from 'src/app/data-request/model';
import { toRequestState } from 'src/app/data-request/operators';
import { IApiProductionLine } from 'src/app/models_new/classes/api-models/ApiProduction_line';
import { pagesPATH } from 'src/app/models_new/config/pages';
import { FilterType, IFilterData } from 'src/app/models_new/types/filter-data';
import {
  FilterTableData,
  ISortingOption,
} from 'src/app/models_new/types/sorting-option';
import { IOrganizationContextResolverData } from 'src/app/resolvers/organization-context-resolver.resolver';
import { ObjectUtils } from 'src/app/utils/object';
import { ProdlineApiService } from '../../../services/api/prodline-api.service';
import { NotificationService } from '../../../services/notification.service';
import { ITableAction, ITableData } from '../../gui/table/table.component';

type ProductionLineActions = 'modify' | 'delete' | 'project';

@Component({
  selector: 'app-inventory-production-lines',
  templateUrl: './inventory-production-lines.component.html',
  styleUrls: ['./inventory-production-lines.component.scss'],
})
export class InventoryProductionLinesComponent implements OnInit, OnDestroy {
  prodlines$: Observable<DataRequestState<ITableData<ProductionLineActions>[]>>;
  destroy$: Subject<boolean> = new Subject<boolean>();
  blockSelected: string[] = [];
  searchText: string = '';
  displayedColumns: string[] = [
    'select',
    'name',
    'hours_of_production',
    'minutes_of_production',
    'projects',
  ];

  resultFilter: IFilterData[] = [
    {
      filterType: FilterType.SLIDER,
      id: 'hours_of_production',
      label: 'Hours of Production',
      value: 0,
      min: 0,
      max: 24,
    },
    {
      filterType: FilterType.SLIDER,
      id: 'minutes_of_production',
      label: 'Minutes of Production',
      value: 0,
      min: 0,
      max: 60,
    },
  ];

  setupFilter: ISortingOption[] = [];

  sortingColumns: ISortingOption[] = [
    {
      id: 'name',
      label: 'Name',
    },
    {
      id: 'hours_of_production',
      label: 'Hours of Production',
    },
    {
      id: 'minutes_of_production',
      label: 'Minutes of Production',
    },
  ];

  tableFilter: FilterTableData = new FilterTableData();
  orderBy: { column: string; order: SortDirection };
  objUtil = ObjectUtils;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private prodlineApi: ProdlineApiService,
    private notifier: NotificationService
  ) {}

  ngOnInit(): void {
    this.prodlines$ = this.route.data.pipe(
      take(1),
      switchMap((data: Data) =>
        this.prodlineApi.fetchProductionLines(
          (data as IOrganizationContextResolverData).organization_id
        )
      ),
      takeUntil(this.destroy$),
      tap((prodlines) => {
        // Populate setup filter
        for (let prodline of prodlines) {
          for (let option of this.setupFilter) {
            const project_names =
              prodline.production_line_palletizing_projects.map(
                (prodLine) => prodLine.palletizing_project.name
              );
            for (let name of project_names) {
              if (option.children.filter((o) => o.id === name).length === 0)
                option.children.push({ id: name, label: name });
            }
          }
        }
      }),
      map((lines: IApiProductionLine[]) => {
        let tableData: ITableData<ProductionLineActions>[] = [];
        for (let line of lines) {
          tableData.push({
            data: {
              id: line.id,
              name: line.name,
              minutes_of_production: line.data.mop ?? 0,
              hours_of_production: line.data.hop,
              updated_at: line.updated_at,
              projects:
                line.production_line_palletizing_projects.length > 0
                  ? line.production_line_palletizing_projects.map(
                      (prodLine) => prodLine.palletizing_project
                    )
                  : [],
            },
            actions: [
              {
                label: 'Modify',
                actionId: 'modify',
                roleAction: 'modify_production_line',
              },
              {
                label: 'Delete',
                actionId: 'delete',
                txtColor: 'red',
                roleAction: 'delete_production_line',
              },
            ],
          });
        }
        return tableData;
      }),
      toRequestState()
    );
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  onNew() {
    this.router.navigate([pagesPATH.PRODUCTION_LINES, 'new']);
  }

  onDelete() {
    if (this.blockSelected.length === 0) {
      this.notifier.showError('Nothing is selected!');
      return;
    }

    this.deleteProdlinesAction(this.blockSelected)
      .pipe(take(1))
      .subscribe((result) => {
        if (result[0]) {
          this.notifier.showMessage(
            `The production lines were deleted successfully!`
          );
        } else {
          this.notifier.showError(
            'Unable to delete production line! Do you have the correct user privilege in your organization?'
          );
        }
      });
  }

  actionClicked(e: ITableAction<ProductionLineActions>) {
    if (e.actionId === 'modify') {
      this.navigateToProductionLine(e.element.id);
    }
    // Delete action
    if (e.actionId === 'delete') {
      this.deleteProdlinesAction([e.element])
        .pipe(take(1))
        .subscribe((result) => {
          if (result[0]) {
            this.notifier.showMessage(
              `The production line were deleted successfully!`
            );
          } else {
            this.notifier.showError(
              'Unable to delete production line! Do you have the correct user privilege in your organization?'
            );
          }
        });
    }
  }

  navigateToProductionLine(id: string): void {
    this.route.params.pipe(take(1)).subscribe((_data) => {
      this.router.navigate([pagesPATH.PRODUCTION_LINES, id]);
    });
  }

  deleteProdlinesAction(prod_lines: any[]) {
    return this.notifier
      .deletePrompt(
        'Delete',
        'production line',
        prod_lines.map((m) => m.name)
      )
      .afterDismissed()
      .pipe(
        take(1),
        filter(Boolean),
        switchMap(() => {
          return this.deleteProdlines(prod_lines);
        })
      );
  }

  deleteProdlines(prod_lines: any[]): Observable<any> {
    const operations = {};
    for (let i = 0; i < prod_lines.length; i++) {
      operations[i] = this.prodlineApi.deleteProdline(prod_lines[i].id);
    }
    return forkJoin(operations);
  }
}
